import iwexaLogo from '@/assets/iwexa_logo_cropped.png';
import loginBg from '@/assets/login-bg.jpg';
import InputError from '@/components/input-error';
import { LanguageSwitcher } from '@/components/language-switcher';
import TextLink from '@/components/text-link';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useTranslations } from '@/i18n';
import { register } from '@/routes';
import { store } from '@/routes/login';
import { request } from '@/routes/password';
import { Form, Head } from '@inertiajs/react';
import { Lock, Mail } from 'lucide-react';

interface LoginProps {
    status?: string;
    canResetPassword: boolean;
    canRegister: boolean;
}

export default function Login({
    status,
    canResetPassword,
    canRegister,
}: LoginProps) {
    const { t } = useTranslations();
    return (
        <div
            className="flex min-h-screen items-center justify-center bg-cover bg-center p-6"
            style={{ backgroundImage: `url(${loginBg})` }}
        >
            <Head title={t('Log in')} />

            <div className="w-full max-w-xl rounded-3xl border border-gray-200 bg-white px-12 py-6 shadow-2xl md:px-16 md:py-8">
                <div className="flex justify-end">
                    <LanguageSwitcher compact />
                </div>

                <div className="mb-2 text-center">
                    <img src={iwexaLogo} alt="Iwexa" className="mx-auto h-28" />
                </div>

                {status && (
                    <div className="mb-4 text-center text-sm font-medium text-green-600">
                        {status}
                    </div>
                )}

                <Form
                    {...store.form()}
                    resetOnSuccess={['password']}
                    className="space-y-3"
                >
                    {({ processing, errors }) => (
                        <>
                            <div className="space-y-2">
                                <Label
                                    htmlFor="email"
                                    className="text-sm font-semibold tracking-wide text-gray-700"
                                >
                                    {t('Email address')}
                                </Label>
                                <div className="group relative">
                                    <Mail className="absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2 text-gray-400 transition-colors group-focus-within:text-primary" />
                                    <Input
                                        id="email"
                                        type="email"
                                        name="email"
                                        required
                                        autoFocus
                                        tabIndex={1}
                                        className="h-14 rounded-xl border-gray-300 bg-gray-50 pl-12 text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-primary/20"
                                        autoComplete="email"
                                        placeholder="email@example.com"
                                    />
                                </div>
                                <InputError message={errors.email} />
                            </div>

                            <div className="space-y-2">
                                <Label
                                    htmlFor="password"
                                    className="text-sm font-semibold tracking-wide text-gray-700"
                                >
                                    {t('Password')}
                                </Label>
                                <div className="group relative">
                                    <Lock className="absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2 text-gray-400 transition-colors group-focus-within:text-primary" />
                                    <Input
                                        id="password"
                                        type="password"
                                        name="password"
                                        required
                                        tabIndex={2}
                                        autoComplete="current-password"
                                        placeholder={t('Password')}
                                        className="h-14 rounded-xl border-gray-300 bg-gray-50 pl-12 text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-primary/20"
                                    />
                                </div>
                                <InputError message={errors.password} />
                            </div>

                            <div className="flex items-center justify-between pt-2">
                                <div className="flex items-center space-x-3">
                                    <Checkbox
                                        id="remember"
                                        name="remember"
                                        tabIndex={3}
                                    />
                                    <Label htmlFor="remember">
                                        {t('Remember me')}
                                    </Label>
                                </div>
                                {canResetPassword && (
                                    <TextLink
                                        href={request()}
                                        className="text-sm font-medium text-primary"
                                        tabIndex={5}
                                    >
                                        {t('Forgot password?')}
                                    </TextLink>
                                )}
                            </div>

                            <Button
                                type="submit"
                                className="mt-3 h-11 w-full rounded-xl bg-gradient-to-r from-primary to-primary/85 text-base font-bold text-white shadow-lg shadow-primary/30 transition-all hover:scale-[1.02] hover:from-primary/90 hover:to-primary/75 hover:shadow-primary/50 disabled:opacity-50 disabled:hover:scale-100"
                                tabIndex={4}
                                disabled={processing}
                                data-test="login-button"
                            >
                                {processing && <Spinner />}
                                {t('Log in')}
                            </Button>

                            {canRegister && (
                                <>
                                    <div className="relative py-4">
                                        <div className="absolute inset-0 flex items-center">
                                            <div className="w-full border-t border-gray-200" />
                                        </div>
                                        <div className="relative flex justify-center">
                                            <span className="bg-white px-4 text-sm text-gray-400">
                                                {t('or')}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="text-center font-medium text-gray-600">
                                        {t("Don't have an account?")}{' '}
                                        <TextLink
                                            href={register()}
                                            tabIndex={5}
                                            className="font-bold text-primary"
                                        >
                                            {t('Sign up')}
                                        </TextLink>
                                    </div>
                                </>
                            )}

                            <div className="mt-3 border-t border-gray-200 pt-4 text-center text-sm text-gray-400">
                                <p className="font-medium">{t('Need help?')}</p>
                                <a
                                    href="mailto:info@iwexa.com"
                                    className="font-semibold text-primary transition-colors hover:text-primary/80"
                                >
                                    info@iwexa.com
                                </a>
                            </div>
                        </>
                    )}
                </Form>
            </div>
        </div>
    );
}
